import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface AccountManagerProfile {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  title: string | null;
  is_active: boolean;
  max_clients: number;
  specialty: string[] | null;
  avg_response_time_mins: number | null;
  avg_csat_score: number | null;
  total_clients_managed: number;
  created_at: string;
  updated_at: string;
}

export function useAccountManager() {
  const { user, loading: authLoading } = useAuth();
  const [accountManager, setAccountManager] = useState<AccountManagerProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchAM = async () => {
      if (!user) {
        if (isMounted) {
          setAccountManager(null);
          setLoading(false);
        }
        return;
      }

      const { data, error } = await supabase
        .from("account_managers")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!isMounted) return;

      if (error) {
        console.error("[AM] Failed to fetch account manager profile:", error);
        setAccountManager(null);
      } else {
        setAccountManager(data as AccountManagerProfile | null);
      }
      setLoading(false);
    };

    if (authLoading) return;
    fetchAM();

    return () => { isMounted = false; };
  }, [user, authLoading]);

  return {
    accountManager,
    isAccountManager: !!(accountManager && accountManager.is_active),
    loading,
  };
}
